import { HandoverApi } from "@/api/handover/HandoverApi";
import { useAuth } from "@/auth/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function usePendingHandovers() {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ["handover", "pending", user?.id ?? ""],
    queryFn: () => HandoverApi.listPendingLabeled(),
    enabled: !!user?.id,
    staleTime: 15_000,
  });
  return {
    handovers: q.data ?? [],
    loading: q.isLoading,
    refetch: q.refetch,
  };
}

export function useCaseHandovers(caseId: string | undefined, enabled: boolean) {
  const q = useQuery({
    queryKey: ["handover", "case", caseId ?? ""],
    queryFn: () => HandoverApi.listForCase(caseId!),
    enabled: !!caseId && enabled,
  });
  return { handovers: q.data ?? [], refetch: q.refetch };
}

export function useInitiateHandover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: HandoverApi.initiate,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["handover"] });
      void qc.invalidateQueries({ queryKey: ["board"] });
    },
  });
}

export function useAcceptHandover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { handoverId: string; toPlaceId?: string | null }) =>
      HandoverApi.accept(input.handoverId, input.toPlaceId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["handover"] });
      void qc.invalidateQueries({ queryKey: ["board"] });
      void qc.invalidateQueries({ queryKey: ["cases"] });
      void qc.invalidateQueries({ queryKey: ["care"] });
    },
  });
}

export function useCancelHandover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { handoverId: string; reason?: string | null }) =>
      HandoverApi.cancel(input.handoverId, input.reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["handover"] });
    },
  });
}

export function useRejectHandover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { handoverId: string; reason?: string | null }) =>
      HandoverApi.reject(input.handoverId, input.reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["handover"] });
    },
  });
}
